import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { UseFilterActiveFilterTypedFilters } from "@/components/filter/use-filter.hook.tsx";
import {
  ApiAnalyticsTimeOffBalanceReport,
  ApiAnalyticsTimeOffBalanceReportDataRow,
} from "@/types/analytics/analytics.time-off.types.ts";
import { ApiSorting } from "@/types/api.types.ts";
import { EmployeeColumnId } from "@/types/column.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { Table, Text } from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface Props {
  report: ApiAnalyticsTimeOffBalanceReport | undefined;
  isLoading: boolean;
  typedFilters: UseFilterActiveFilterTypedFilters;
  onApplySorting: (sorting: ApiSorting) => void;
}

enum TimeOffBalancesByCategoryTableColumnId {
  balance_start = "balance_start",
  accrued_amount = "accrued_amount",
  spent_amount = "spent_amount",
  booked_amount = "booked_amount",
  expired_amount = "expired_amount",
  balance_end = "balance_end",
}

export function TimeOffBalancesByCategoryTable({
  report,
  isLoading,
  typedFilters,
  onApplySorting,
}: Props) {
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<ApiAnalyticsTimeOffBalanceReportDataRow>[]>(() => {
    const inner: ColumnDef<ApiAnalyticsTimeOffBalanceReportDataRow>[] = [];
    inner.push({
      id: EmployeeColumnId.employee_name,
      accessorKey: EmployeeColumnId.employee_name,
      header: t("core:full_name"),
      cell: (cell) => <EmployeeItem enablePreview={true} employee={cell.row.original.employee} />,
      ...ColumnSizes.employee,
      enableSorting: true,
      sortDescFirst: false,
      ...ColumnSizes.employee,
    });
    inner.push({
      id: TimeOffBalancesByCategoryTableColumnId.balance_start,
      accessorKey: TimeOffBalancesByCategoryTableColumnId.balance_start,
      header: t("time_off:accrual_action_initial_balance"),
      cell: (cell) => <Text>{cell.row.original.balance_start} </Text>,
      ...ColumnSizes.status,
      enableSorting: true,
      sortDescFirst: false,
      ...ColumnSizes.standard,
    });

    inner.push({
      id: TimeOffBalancesByCategoryTableColumnId.accrued_amount,
      accessorKey: TimeOffBalancesByCategoryTableColumnId.accrued_amount,
      header: t("time_off:accrual_action_accrual"),
      cell: (cell) => <Text>{cell.row.original.accrued_amount} </Text>,
      ...ColumnSizes.status,
      enableSorting: true,
      sortDescFirst: false,
      ...ColumnSizes.standard,
    });
    inner.push({
      id: TimeOffBalancesByCategoryTableColumnId.spent_amount,
      accessorKey: TimeOffBalancesByCategoryTableColumnId.spent_amount,
      header: t("time_off:accrual_action_used"),
      cell: (cell) => <Text>{cell.row.original.spent_amount} </Text>,
      ...ColumnSizes.status,
      enableSorting: true,
      sortDescFirst: false,
      ...ColumnSizes.standard,
    });
    inner.push({
      id: TimeOffBalancesByCategoryTableColumnId.booked_amount,
      accessorKey: TimeOffBalancesByCategoryTableColumnId.booked_amount,
      header: t("time_off:accrual_action_booked"),
      cell: (cell) => <Text>{cell.row.original.booked_amount} </Text>,
      ...ColumnSizes.status,
      enableSorting: true,
      sortDescFirst: false,
      ...ColumnSizes.standard,
    });

    inner.push({
      id: TimeOffBalancesByCategoryTableColumnId.expired_amount,
      accessorKey: TimeOffBalancesByCategoryTableColumnId.expired_amount,
      header: t("time_off:accrual_action_expire"),
      cell: (cell) => <Text>{cell.row.original.expired_amount} </Text>,
      ...ColumnSizes.status,
      enableSorting: true,
      sortDescFirst: false,
      ...ColumnSizes.standard,
    });
    inner.push({
      id: TimeOffBalancesByCategoryTableColumnId.balance_end,
      accessorKey: TimeOffBalancesByCategoryTableColumnId.balance_end,
      header: t("reporting:ending_balance"),
      cell: (cell) => <Text>{cell.row.original.balance_end} </Text>,
      ...ColumnSizes.status,
      enableSorting: true,
      sortDescFirst: false,
      ...ColumnSizes.standard,
    });

    return inner;
  }, [report]);

  return (
    <Table
      data={report?.report.rows || []}
      columns={columns}
      showSkeleton={isLoading}
      stickyHeader={true}
      sorting={typedFilters.sorting}
      onSortUpdated={onApplySorting}
      stickyFirstColumn={true}
    />
  );
}
